import { PaymentsContext } from '@powerupsclub/payments';
import { TrelloPowerUpContext } from '@powerupsclub/trello';
import Spinner from '@atlaskit/spinner';
import React, { useContext, useEffect, useLayoutEffect, useState } from 'react';
import { Poll } from '../../types/models';
import ProgressBar from '@ramonak/react-progress-bar';
import Button from '@atlaskit/button';
import Textfield from '@atlaskit/textfield';
import CheckIcon from '@atlaskit/icon/glyph/check';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import AvatarGroup from '@atlaskit/avatar-group';
import { Member } from '@powerupsclub/trello/dist/types';
import {
  getPollsByIdCard,
  setPollsByIdCard,
} from '../../api/pollsInPluginData';
import { getTokenValue } from '@atlaskit/tokens';

async function getMembersMap(idMembers: string[], trelloIframe: any) {
  const membersMap: { [id: string]: Member } = {};

  const board = await trelloIframe.board('all');

  for (let member of board.members) {
    membersMap[member.id] = member;
  }

  return membersMap;
}

type PollResultsProps = {
  poll: Poll;
  index: number;
  polls: Poll[];
  membersMap: { [id: string]: Member };
};

const PollResults: React.FC<PollResultsProps> = ({
  poll,
  polls,
  index,
  membersMap,
}) => {
  const { me, trelloIframe } = useContext(TrelloPowerUpContext);

  const numberOfVotes = poll.options.reduce((total, option) => {
    return total + option.idMemberVotes.length;
  }, 0);

  const indexOfHighestVote = poll.options.reduce(
    (highestIndex, option, idx) => {
      if (
        option.idMemberVotes.length >
        poll.options[highestIndex].idMemberVotes.length
      ) {
        return idx;
      }

      return highestIndex;
    },
    0
  );

  const onChangeVote = async () => {
    const newPoll = { ...poll };
    for (const option of newPoll.options) {
      option.idMemberVotes = option.idMemberVotes.filter(
        (idMember) => idMember !== me!.id
      );
    }

    const newPolls = [...polls];
    newPolls[index] = newPoll;
    await setPollsByIdCard(trelloIframe, newPolls);
  };

  return (
    <div style={{ marginTop: '8px' }}>
      {poll.options.map((option, optionIndex) => {
        const percentage = (option.idMemberVotes.length / numberOfVotes) * 100;

        const voterAvatars = option.idMemberVotes.map((idMember) => {
          const member = membersMap[idMember];

          return {
            name: member?.fullName ?? 'Member not on board',
            src: member?.avatar ?? '',
          };
        });

        return (
          <div
            style={{
              marginBottom: '8px',
              position: 'relative',
            }}
          >
            <ProgressBar
              key={optionIndex}
              completed={percentage}
              isLabelVisible={false}
              height="32px"
              bgColor={
                indexOfHighestVote === optionIndex
                  ? getTokenValue('color.chart.blue.bold', '#4C9AFF')
                  : getTokenValue('color.chart.gray.bold', '#DEEBFF')
              }
              baseBgColor={getTokenValue(
                'elevation.surface.raised',
                'rgb(244, 245, 247)'
              )}
            />
            <div
              style={{
                position: 'absolute',
                top: '8px',
                left: '16px',
                display: 'flex',
                right: '16px',
              }}
            >
              <div style={{ marginRight: '16px', minWidth: '40px' }}>
                <b>{percentage.toFixed(0)}%</b>
              </div>
              <div
                style={{
                  maxWidth: '360px',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}
              >
                {option.value}
              </div>
              <div style={{ position: 'absolute', right: 0, top: '-2px' }}>
                <AvatarGroup
                  data={voterAvatars}
                  size="xsmall"
                  maxCount={8}
                  onMoreClick={(e) => {
                    trelloIframe.popup({
                      title: `${option.idMemberVotes.length} votes`,
                      mouseEvent: e,
                      url: `./votersPopup/${index}/${optionIndex}`,
                      args: {
                        poll,
                        option,
                      },
                    });
                  }}
                />
              </div>
            </div>
          </div>
        );
      })}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div
          style={{
            marginLeft: '16px',
          }}
        >
          {numberOfVotes} votes
        </div>
        <div>
          <Button appearance="subtle-link" onClick={onChangeVote}>
            Change vote
          </Button>
        </div>
      </div>
    </div>
  );
};

type PollElementProps = {
  poll: Poll;
  showResults?: boolean;
  onVote: (index: number | number[]) => void;
  index: number;
  polls: Poll[];
  membersMap: { [id: string]: Member };
};

const PollElement: React.FC<PollElementProps> = ({
  poll,
  showResults,
  onVote,
  index,
  polls,
  membersMap,
}) => {
  const [newOptionText, setNewOptionText] = useState('');
  const [showNewOptionField, setShowNewOptionField] = useState(false);
  const { trelloIframe } = useContext(TrelloPowerUpContext);

  const [multiVoteIndexes, setMultiVoteIndexes] = useState<number[]>([]);

  const onNewOptionSubmit = async () => {
    const newOptions = [...poll.options];
    newOptions.push({
      value: newOptionText,
      idMemberVotes: [],
    });

    const newPolls = [...polls];
    newPolls[index].options = newOptions;
    await setPollsByIdCard(trelloIframe, newPolls);

    setShowNewOptionField(false);
  };

  const onVoteHandler = (index: number) => {
    if (!poll.settings.allowMultipleVotes) {
      onVote(index);
      return;
    }

    let newVotes = [...multiVoteIndexes];

    if (multiVoteIndexes.includes(index)) {
      newVotes = multiVoteIndexes.filter((i) => i !== index);
    } else {
      newVotes.push(index);
    }

    setMultiVoteIndexes(newVotes);
  };

  return (
    <div
      style={{
        color: getTokenValue('color.text', 'rgb(23, 43, 77)'),
        fontSize: '14px',
        marginBottom: '24px',
      }}
    >
      <b>{poll.name}</b>

      {showResults ? (
        <PollResults
          poll={poll}
          polls={polls}
          index={index}
          membersMap={membersMap}
        />
      ) : (
        <div style={{ marginTop: '8px' }}>
          {poll.options.map((option, index) => (
            <div style={{ marginBottom: '8px' }}>
              <Button
                onClick={() => onVoteHandler(index)}
                shouldFitContainer
                appearance={
                  multiVoteIndexes.includes(index) ? 'warning' : 'default'
                }
              >
                {option.value}
              </Button>
            </div>
          ))}
          {poll.settings.allowCustomOptions && (
            <div>
              {showNewOptionField ? (
                <div style={{ display: 'flex' }}>
                  <Textfield
                    placeholder="Add another option"
                    value={newOptionText}
                    onChange={(e) => setNewOptionText(e.currentTarget.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && newOptionText.length) {
                        onNewOptionSubmit();
                      }
                    }}
                  />
                  <Button
                    style={{
                      height: 'auto',
                      minWidth: '32px',
                      marginLeft: '4px',
                    }}
                    iconBefore={<CheckIcon label="submit" size="small" />}
                    appearance="primary"
                    onClick={onNewOptionSubmit}
                    isDisabled={newOptionText.length === 0}
                  />
                  <Button
                    style={{
                      height: 'auto',
                      minWidth: '32px',
                      marginLeft: '4px',
                    }}
                    iconBefore={<CrossIcon label="cancel" size="small" />}
                    appearance="default"
                    onClick={() => setShowNewOptionField(false)}
                  />
                </div>
              ) : (
                <Button
                  onClick={() => {
                    setNewOptionText('');
                    setShowNewOptionField(true);
                  }}
                  shouldFitContainer
                  appearance="subtle"
                >
                  Add another option
                </Button>
              )}
            </div>
          )}

          {poll.settings.allowMultipleVotes && (
            <div style={{ marginTop: '8px' }}>
              <Button
                appearance="primary"
                shouldFitContainer
                isDisabled={multiVoteIndexes.length === 0}
                onClick={() => onVote(multiVoteIndexes)}
              >
                Submit vote
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const Polls: React.FC = () => {
  const {
    isLoading: isLoadingTrello,
    me,
    trelloIframe,
    board,
    token,
  } = useContext(TrelloPowerUpContext);
  const { isLoading: isLoadingPayments } = useContext(PaymentsContext);

  const [polls, setPolls] = useState<Poll[]>([]);
  const [membersMap, setMembersMap] = useState<{ [id: string]: Member }>({});
  const [isLoadingPolls, setIsLoadingPolls] = useState(true);

  const isLoading = isLoadingTrello || isLoadingPayments || isLoadingPolls;

  useLayoutEffect(() => {
    trelloIframe.sizeTo('#root');
    setTimeout(() => trelloIframe.sizeTo('#root'), 1000);
  }, [polls, trelloIframe]);

  useEffect(() => {
    async function loadPolls() {
      setIsLoadingPolls(true);
      let _polls: Poll[] = await getPollsByIdCard(trelloIframe);

      // Deprecation of membersMap because of pluginData size limit.
      if (_polls.length) {
        _polls = _polls.map((poll) => {
          delete poll.membersMap;

          return poll;
        });

        await setPollsByIdCard(trelloIframe, _polls);
      }

      const idMembers = new Set<string>();

      _polls.forEach((poll) => {
        poll.options.forEach((option) => {
          option.idMemberVotes.forEach((idMember) => {
            idMembers.add(idMember);
          });
        });
      });

      const _membersMap = await getMembersMap(
        Array.from(idMembers),
        trelloIframe
      );

      setMembersMap(_membersMap);
      setPolls(_polls);
      setIsLoadingPolls(false);
    }

    trelloIframe.render(() => loadPolls());
  }, [board?.id, token, trelloIframe]);

  if (isLoading) {
    return (
      <div
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Spinner size="large" />
      </div>
    );
  }

  return (
    <>
      {polls.map((poll: Poll, index) => {
        const memberHasVoted = poll.options.some((option) =>
          option.idMemberVotes.includes(me!.id)
        );

        const onVote = async (optionIndex: number | number[]) => {
          const poll = polls[index];

          const optionIndexes = Array.isArray(optionIndex)
            ? optionIndex
            : [optionIndex];

          for (const optionIdx of optionIndexes) {
            poll.options[optionIdx].idMemberVotes.push(me!.id);
          }

          // Deprecation of membersMap because of pluginData size limit.
          if (poll.membersMap) {
            poll.membersMap = undefined;
          }

          await setPollsByIdCard(trelloIframe, polls);
          setPolls([...polls]);
        };

        return (
          <PollElement
            key={`poll-${index}`}
            poll={poll}
            polls={polls}
            membersMap={membersMap}
            index={index}
            showResults={memberHasVoted}
            onVote={onVote}
          />
        );
      })}
    </>
  );
};
